<template>
  <div
    :id="carouselIdentifier"
    class="carousel"
    :class="{ 'carousel-vertical': vertical, 'carousel-text-selectable': !swipeOnDesktop }"
  >
    <div class="carousel-wrapper">
      <div
        class="carousel-inner"
        :style="{
          transform: `translate${vertical ? 'Y' : 'X'}(${currentOffset}px)`,
          transition: transitionStyle,
          flexBasis: `${slideWidth}px`,
          msFlexPreferredSize: `${slideWidth}px`,
          visibility: `${slideWidth ? 'visible' : 'hidden'}`,
        }"
      >
        <slot></slot>
      </div>
    </div>
    <pagination v-if="!paginationtype && paginationEnabled && pageCount > 0" />
    <quick-link-pagination v-if="paginationtype && paginationEnabled && pageCount > 0" />
    <navigation v-if="navigationEnabled" />
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import Navigation from './navigation.vue';
import Pagination from './pagination.vue';
import quickLinkPagination from './quickLinkPagination.vue';
export default {
  name: 'carousel',
  components: {
    Navigation,
    pagination: Pagination,
    quickLinkPagination,
  },
  data() {
    return {
      browserWidth: null,
      carouselSize: null,
      currentPage: this.startAt,
      prevPage: this.startAt,
      dragOffset: 0,
      dragStartX: 0,
      mousedown: false,
      slideCount: 0,
      maxSwipeAngle: 60,
      angle: 0,
    };
  },
  beforeUpdate() {
    this.computeCarouselSize();
  },
  props: {
    // Slide transition easing. any valid CSS transition easing accepted
    easing: { type: String, default: 'ease' },

    // Minimum distance for the swipe to trigger a slide advance
    minSwipeDistance: { type: Number, default: 40 },

    // pagination options
    paginationtype: { type: Boolean, default: false },

    // Flag to render the navigation component (next/prev buttons)
    navigationEnabled: { type: Boolean, default: false },

    // Flag to render pagination component
    paginationEnabled: { type: Boolean, default: true },

    // Maximum number of slides displayed on each page
    perPage: { type: Number, default: 2 },

    // Vertical instead of horizontal
    vertical: { type: Boolean, default: false },

    // the offset between items
    slideSpacing: { type: Object, default: () => {} },

    // padding for the slides
    slidePadding: { type: Number, default: 0 },

    /**
     * Configure the number of visible slides with a particular browser width.
     * This will be an array of arrays, ex. [[320, 2], [1199, 4]]
     * Formatted as [x, y] where x=browser width, and y=number of slides displayed.
     * ex. [1199, 4] means if (window <= 1199) then show 4 slides per page
     */
    perPageCustom: { type: Array },

    // Scroll per page, not per item
    scrollPerPage: { type: Boolean, default: false },

    // Slide transition speed. Number of milliseconds accepted
    speed: { type: Number, default: 400 },

    // Start at slide
    startAt: { type: Number, default: 0 },

    // Start at slide
    carouselIdentifier: { type: String, default: `carousel-${Date.now()}` },

    // disable or enable swipe on desktop for Sitecore edit mode
    swipeOnDesktop: { type: Boolean, default: true },
  },
  computed: {
    /**
     * Given a viewport width, find the number of slides to display
     * @param  {Number} width Current viewport width in pixels
     * @return {Number}       Number of slides to display
     */
    breakpointSlidesPerPage() {
      if (!this.perPageCustom) {
        return this.perPage;
      }

      const breakpointArray = this.perPageCustom;
      const width = this.browserWidth;

      const breakpoints = breakpointArray.sort((a, b) => (a[0] > b[0] ? -1 : 1));

      // Reduce the breakpoints to entries where the width is in range
      // The breakpoint arrays are formatted as [widthToMatch, numberOfSlides]
      const matches = breakpoints.filter((breakpoint) => width >= breakpoint[0]);

      // If there is a match, the result should return only
      // the slide count from the first matching breakpoint
      const match = matches[0] && matches[0][1];

      return match || this.perPage;
    },

    /**
     * @return {Boolean} Can the slider move forward?
     */
    canAdvanceForward() {
      return this.currentPage < this.pageCount - 1;
    },

    /**
     * @return {Boolean} Can the slider move backward?
     */
    canAdvanceBackward() {
      return this.currentPage > 0;
    },

    /**
     * Number of slides to display per page in the current context.
     * This is constant unless responsive perPage option is set.
     * @return {Number} The number of slides per page to display
     */
    currentPerPage() {
      return !this.perPageCustom || this.$isServer ? this.perPage : this.breakpointSlidesPerPage;
    },

    /**
     * The horizontal distance the inner wrapper is offset while navigating.
     * @return {Number} Pixel value of offset to apply
     */
    currentOffset() {
      const page = this.currentPage;
      const width = this.slideWidth;
      const dragged = this.dragOffset;
      const spacing = this.getSlideSpacing();
      const totalSlides = this.slideCount;
      const perPage = this.currentPerPage;

      let offset;

      // Special case: If we're at the very last slide, show it fully
      if (page >= totalSlides - 1 && !this.scrollPerPage) {
        // Fully align the last slide
        offset = (totalSlides - perPage) * width;
      } else {
        // The offset distance depends on whether the scrollPerPage option is active.
        // If this option is active, the offset will be determined per page rather than per item.
        offset = this.scrollPerPage ? page * width * this.currentPerPage : page * width;
      }
      let currentOffset = (offset + dragged) * -1;

      const htmlLang = document.querySelector('html').getAttribute('lang');
      if (htmlLang === 'he' && !this.vertical) {
        return -(currentOffset + spacing);
      }

      return currentOffset + spacing;
    },

    isHidden() {
      return this.carouselSize <= 0;
    },

    /**
     * Calculate the number of pages of slides
     * @return {Number} Number of pages
     */
    pageCount() {
      const slideCount = this.slideCount;
      const perPage = this.currentPerPage;

      if (this.scrollPerPage) {
        const pages = Math.ceil(slideCount / perPage);
        return pages < 1 ? 1 : pages; // Be sure to not round down to zero pages
      }
      return Math.ceil(slideCount - (this.currentPerPage - 1));
    },

    /**
     * Calculate the width of each slide
     * @return {Number} Slide width
     */
    slideWidth() {
      const width = this.carouselSize;
      const perPage = this.currentPerPage;
      const slideWidth = width / perPage;

      return slideWidth - this.getSlideSpacing() * 2;
    },

    transitionStyle() {
      return `${this.speed / 1000}s ${this.easing} transform`;
    },
  },

  mounted() {
    if (!this.$isServer) {
      window.addEventListener('resize', debounce(this.computeCarouselSize, 16));

      if ('ontouchstart' in window) {
        this.$el.addEventListener('touchstart', this.handleMousedown);
        this.$el.addEventListener('touchend', this.handleMouseup);
        this.$el.addEventListener('touchmove', this.handleMousemove);
      }

      if (this.swipeOnDesktop) {
        this.$el.addEventListener('mousedown', this.handleMousedown);
        this.$el.addEventListener('mouseup', this.handleMouseup);
        this.$el.addEventListener('mousemove', this.handleMousemove);
      }
    }

    if (window.eventBus) {
      window.eventBus.$on(`${this.carouselIdentifier}-gotoPage`, (page) => {
        this.goToPage(page);
      });
    }

    this.attachMutationObserver();
    this.computeCarouselSize();
  },

  /* istanbul ignore next */
  beforeDestroy() {
    if (!this.$isServer) {
      this.detachMutationObserver();
      window.removeEventListener('resize', this.getBrowserWidth);
      if ('ontouchstart' in window) {
        this.$el.removeEventListener('touchmove', this.handleMousemove);
      } else {
        this.$el.removeEventListener('mousemove', this.handleMousemove);
      }
    }
  },
  methods: {
    /**
     * Increase/decrease the current page value
     * @param  {String} direction (Optional) The direction to advance
     */
    advancePage(direction, from = false) {
      if (direction && direction === 'backward' && this.canAdvanceBackward) {
        this.goToPage(this.currentPage - 1, from);
      } else if (
        (!direction || (direction && direction !== 'backward')) &&
        this.canAdvanceForward
      ) {
        this.goToPage(this.currentPage + 1, from);
      }
    },

    /**
     * A mutation observer is used to detect changes to the containing node
     * in order to keep the magnet container in sync with the height its reference node.
     * Note: This does not work for ie10.
     */
    /* istanbul ignore next */
    attachMutationObserver() {
      const MutationObserver =
        window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;

      if (MutationObserver) {
        const config = { attributes: true, data: true };
        this.mutationObserver = new MutationObserver(() => {
          this.$nextTick(() => {
            this.computeCarouselSize();
          });
        });
        if (this.$parent.$el) {
          this.mutationObserver.observe(this.$parent.$el, config);
        }
      }
    },

    getSlideSpacing() {
      let value = 0;
      for (const key in this.slideSpacing) {
        if (Object.prototype.hasOwnProperty.call(this.slideSpacing, key)) {
          if (key <= this.getBrowserWidth()) value = this.slideSpacing[key];
        }
      }
      return value;
    },

    /**
     * Stop listening to mutation changes
     */
    /* instanbul ignore next */
    detachMutationObserver() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    },

    /**
     * Get the current browser viewport width
     * @return {Number} Browser"s width in pixels
     */
    getBrowserWidth() {
      this.browserWidth = window.innerWidth;
      return this.browserWidth;
    },

    /**
     * Get the width of the carousel DOM element
     * @return {Number} Width of the carousel in pixels
     */
    getCarouselSize() {
      if (this.vertical) {
        this.carouselSize = (this.$el && this.$el.clientHeight) || 0; // Assign globally
      } else {
        this.carouselSize = (this.$el && this.$el.clientWidth) || 0; // Assign globally
      }

      return this.carouselSize;
    },

    /**
     * Filter slot contents to slide instances and return length
     * @return {Number} The number of slides
     */
    getSlideCount() {
      // Vue 2: $slots.default is a VNode array, not a render function (Vue 3).
      const defaultSlots = this.$slots && this.$slots.default;
      this.slideCount =
        (defaultSlots &&
          defaultSlots.filter((slot) => slot.tag && slot.tag.indexOf('slide') > -1).length) ||
        0;
    },

    /**
     * Set the current page to a specific value
     * This function will only apply the change if the value is within the carousel bounds
     * @param  {Number} page The value of the new page number
     */
    goToPage(page, from = false) {
      if (page >= 0 && page <= this.pageCount) {
        this.currentPage = page;
        this.$emit('pageChange', this.currentPage);
        const carouselIMG = this.$el.querySelectorAll(
          '.carousel-slide img, .carousel-slide video, .carousel-slide iframe',
        );
        const loadImg = Math.round(this.currentPerPage) - 1;
        const index = loadImg + this.currentPage;
        if (carouselIMG.length) {
          if (!carouselIMG[index].classList.contains('lazyloaded')) {
            carouselIMG[index].classList.add('lazyload');
          }
        }
        const nativeVideoDiv = this.$el.querySelector('.videolazy');
        if (nativeVideoDiv !== null) {
          this.nativevideoLoad(nativeVideoDiv);
        }
        if (window.eventBus) {
          window.eventBus.$emit(
            `${this.carouselIdentifier}-pageChange`,
            this.currentPage,
            this.prevPage,
            from,
          );
        }
        this.prevPage = this.currentPage;
      }
    },
    nativevideoLoad(el) {
      if (typeof el.children === 'object') {
        for (const source in el.children) {
          if (el.children[source].tagName === 'SOURCE') {
            el.children[source].src = el.children[source].dataset.src;
          }
        }
        el.className = 'embed-responsive-item';
        el.load();
      }
    },
    /**
     * Trigger actions when mouse is pressed
     * @param  {Object} e The event object
     */
    handleMousedown(e) {
      if (!e.touches) {
        e.preventDefault();
      }

      this.mousedown = true;
      this.dragStart = e.touches ? e.touches[0] : e;
    },

    /**
     * Trigger actions when mouse is released
     * @param  {Object} e The event object
     */
    handleMouseup() {
      this.mousedown = false;
      this.dragOffset = 0;
    },

    /**
     * We know the main Distance swiped and the sub direction distance swiped
     * based on that we can calculate the swipe angle
     * @param {number} Opposite
     * @param {number} Adjacent
     * @returns {number} the angle based on Tangent: tan(Î¸) = Opposite / Adjacent
     */
    swipeAngle(main, sub) {
      return Math.atan(Math.abs(sub) / Math.abs(main)) * (180 / Math.PI);
    },

    /**
     * Trigger actions when mouse is pressed and then moved (mouse drag)
     * @param  {Object} e The event object
     */
    handleMousemove(e) {
      if (!this.mousedown) return;

      const event = e.touches ? e.touches[0] : e;

      let mainDirectionDragDistance = 0;
      let subDirectionDragDistance = 0;

      if (this.vertical) {
        mainDirectionDragDistance = this.dragStart.clientY - event.clientY;
        subDirectionDragDistance = this.dragStart.clientX - event.clientX;
      } else {
        mainDirectionDragDistance = this.dragStart.clientX - event.clientX;
        subDirectionDragDistance = this.dragStart.clientY - event.clientY;
      }

      this.angle = this.swipeAngle(mainDirectionDragDistance, subDirectionDragDistance);

      /**
       * If the swipe angle is less then the max swipe angle then
       * the user is probably using the slideshow to see the next slide
       *
       * We want to use prevent default to prevent the page from scrolling when switching to a new slide.
       */
      if (Math.abs(this.angle) <= this.maxSwipeAngle) {
        e.preventDefault();
      }

      this.dragOffset = mainDirectionDragDistance;

      if (this.dragOffset > this.minSwipeDistance) {
        this.handleMouseup();
        this.advancePage();
      } else if (this.dragOffset < -this.minSwipeDistance) {
        this.handleMouseup();
        this.advancePage('backward');
      }
    },

    /**
     * Re-compute the width of the carousel and its slides
     */
    /* istanbul ignore next */
    computeCarouselSize() {
      this.getSlideCount();
      this.getBrowserWidth();
      this.getCarouselSize();
      this.setCurrentPageInBounds();
    },

    /**
     * When the current page exceeds the carousel bounds, reset it to the maximum allowed
     */
    setCurrentPageInBounds() {
      if (!this.canAdvanceForward) {
        const setPage = this.pageCount - 1;
        this.currentPage = setPage >= 0 ? setPage : 0;
      }
    },
  },
};
</script>
